/* ingredient-index.js — certifications + expandable ingredient cards.
   Pairs with sections/ingredient-index.liquid, snippets/ingredient-index-set.liquid
   and ingredient-index.css.

   SCOPE. This file owns:

     1. The card disclosure (chevron -> the long science copy).
     2. The mobile carousel's CONTROLS — arrows, dots, and which dot is lit.
     3. The scroll reveal.
     4. The Morning/Night set toggle (bundle "toggle" mode only).

   It does NOT own the carousel's scrolling, snapping, or its height when a card
   expands. Those are CSS: the track is a scroll-snap flex row, so the browser
   does the swiping, the snapping, and the growing.

   PANELS. A section renders one OR MORE "sets" (`[data-ii-set]`): one in single
   mode, two (Morning + Night) in toggle mode. Everything track-shaped — cards,
   disclosure, equalize, carousel — is owned by a Panel, one per set, so the two
   carousels in a bundle never share state. The section owns only what is genuinely
   section-wide: the scroll reveal and the set toggle.

   Vanilla, no dependencies, prototype-style to match theme.js and
   product-display.js. GSAP, if present, is an enhancement only. */

window.theme = window.theme || {};

theme.IngredientIndex = (function () {
  var selectors = {
    set: '[data-ii-set]',
    track: '[data-ii-track]',
    card: '[data-ii-card]',
    head: '.ingredient-card__head',
    body: '.ingredient-card__body',
    toggle: '[data-ii-toggle]',
    more: '[data-ii-more]',
    controls: '[data-ii-controls]',
    arrow: '[data-ii-arrow]',
    dots: '[data-ii-dots]',
    dot: '[data-ii-dot]',
    revealItem: '[data-ii-reveal-item]',
    toggleGroup: '[data-ii-toggle-group]',
    setBtn: '[data-ii-set-btn]'
  };

  var classes = {
    open: 'is-open',
    staticCard: 'ingredient-card--static',
    carousel: 'is-carousel',
    armed: 'is-armed',
    cssReveal: 'is-css-reveal',
    in: 'is-in'
  };

  var MOBILE = '(max-width: 768px)';

  function prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* ============================================================= Panel

     One ingredient set: its own track, cards, dots, arrows and active index.
     Registers all its listeners through the owning section so a single onUnload
     tears the whole section down. */
  function Panel(section, el) {
    this.section = section;
    this.el = el;
    this.setKey = el.getAttribute('data-ii-set');

    this.cache = {
      track: el.querySelector(selectors.track),
      cards: el.querySelectorAll(selectors.card),
      controls: el.querySelector(selectors.controls),
      dots: el.querySelectorAll(selectors.dot),
      arrows: el.querySelectorAll(selectors.arrow)
    };

    this.activeIndex = 0;
    this.rafId = null;
  }

  Panel.prototype = {
    init: function () {
      this.initToggles();
      this.initEqualize();
      this.initCarousel();
    },

    /* Called when a hidden set is toggled into view: while it was display:none
       every measurement read 0, so equalize and the active-dot sync have to run
       again now that the cards have real dimensions. */
    activate: function () {
      this.equalize();
      this.syncActive();
    },

    /* ------------------------------------------------------------- equalize

       Every collapsed card is the same height: the tallest sets the bar and the
       rest are padded up to it. Measures HEAD + BODY (the collapsed height), never
       the card box, so an OPEN card cannot inflate the floor and shove its
       neighbours down. The floor is published as --ii-card-min on THIS set, so a
       Morning set of 13 and a Night set of 8 keep independent heights. */
    initEqualize: function () {
      if (!this.cache.cards.length) return;

      this.equalize();

      // Images are cutouts with height:auto — 0 tall until loaded, so every card
      // measures short until then. window.load is the honest signal.
      this.section.on(window, 'load', this.equalize.bind(this));
      this.section.on(window, 'resize', this.section.debounce(this.equalize.bind(this), 150));

      // A webfont swap reflows every line the measurement depends on.
      if (document.fonts && document.fonts.ready) {
        var self = this;
        document.fonts.ready.then(function () {
          if (!self.section.destroyed) self.equalize();
        });
      }
    },

    equalize: function () {
      var cards = this.cache.cards;
      if (!cards.length) return;

      // Clear the previous floor BEFORE measuring, or the cards can only grow.
      this.el.style.removeProperty('--ii-card-min');

      var tallest = 0;

      for (var i = 0; i < cards.length; i++) {
        var head = cards[i].querySelector(selectors.head);
        var body = cards[i].querySelector(selectors.body);
        if (!head || !body) continue;

        var cs = window.getComputedStyle(cards[i]);
        var borders = parseFloat(cs.borderTopWidth) + parseFloat(cs.borderBottomWidth);

        var h =
          head.getBoundingClientRect().height +
          body.getBoundingClientRect().height +
          borders;

        if (h > tallest) tallest = h;
      }

      // A hidden (display:none) set measures 0 — leave the floor unset until it is
      // shown and activate() re-runs this.
      if (!tallest) return;

      this.el.style.setProperty('--ii-card-min', Math.ceil(tallest) + 'px');
    },

    /* ----------------------------------------------------------- disclosure */

    initToggles: function () {
      var cards = this.cache.cards;

      for (var i = 0; i < cards.length; i++) {
        var card = cards[i];
        var toggle = card.querySelector(selectors.toggle);
        var more = card.querySelector(selectors.more);

        if (!toggle) continue;

        /* No science copy -> no disclosure region -> the button controls nothing.
           Strip it back to inert text rather than leave a chevron opening a void. */
        if (!more) {
          card.classList.add(classes.staticCard);
          toggle.removeAttribute('aria-expanded');
          toggle.removeAttribute('aria-controls');
          toggle.setAttribute('tabindex', '-1');
          toggle.disabled = true;
          continue;
        }

        this.section.on(toggle, 'click', this.onToggle.bind(this));
      }
    },

    /* Cards are independent — opening one never closes another, so a shopper can
       hold two ingredients side by side and compare. */
    onToggle: function (evt) {
      var toggle = evt.currentTarget;
      var card = toggle.closest(selectors.card);
      if (!card) return;

      var open = !card.classList.contains(classes.open);

      card.classList.toggle(classes.open, open);
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');

      // On mobile the card just changed height inside a scroll container; re-sync
      // the lit dot. A no-op on desktop.
      this.syncActive();
    },

    /* ------------------------------------------------------------- carousel

       No slider library. The track is a CSS scroll-snap row; this only builds the
       controls. `is-carousel` on the SECTION is the "JS is alive" signal the CSS
       waits for before revealing controls that would otherwise be dead. */
    initCarousel: function () {
      var track = this.cache.track;
      if (!track || !this.cache.cards.length) return;

      this.section.container.classList.add(classes.carousel);

      var i;
      for (i = 0; i < this.cache.arrows.length; i++) {
        this.section.on(this.cache.arrows[i], 'click', this.onArrow.bind(this));
      }
      for (i = 0; i < this.cache.dots.length; i++) {
        this.section.on(this.cache.dots[i], 'click', this.onDot.bind(this));
      }

      this.section.on(
        this.cache.dots.length ? this.el.querySelector(selectors.dots) : null,
        'keydown', this.onDotKeydown.bind(this));

      this.section.on(track, 'scroll', this.onTrackScroll.bind(this), { passive: true });
      this.section.on(window, 'resize', this.section.debounce(this.syncActive.bind(this), 150));

      this.syncActive();
    },

    onTrackScroll: function () {
      if (this.rafId) return;

      var self = this;
      this.rafId = window.requestAnimationFrame(function () {
        self.rafId = null;
        self.syncActive();
      });
    },

    /* Which card is showing? Measured, never counted — the shopper can free-swipe
       between snap points, and the nearest card to the track's left edge is the
       honest answer. Read fresh each time: a resize invalidates cached offsets. */
    syncActive: function () {
      var track = this.cache.track;
      var cards = this.cache.cards;
      if (!track || !cards.length) return;

      var trackLeft = track.getBoundingClientRect().left;
      var best = 0;
      var bestDist = Infinity;

      for (var i = 0; i < cards.length; i++) {
        var dist = Math.abs(cards[i].getBoundingClientRect().left - trackLeft);
        if (dist < bestDist) {
          bestDist = dist;
          best = i;
        }
      }

      this.setActive(best);
    },

    setActive: function (index) {
      this.activeIndex = index;

      var dots = this.cache.dots;
      for (var i = 0; i < dots.length; i++) {
        var on = i === index;
        dots[i].setAttribute('aria-selected', on ? 'true' : 'false');
        dots[i].setAttribute('tabindex', on ? '0' : '-1');
      }

      var arrows = this.cache.arrows;
      for (var j = 0; j < arrows.length; j++) {
        var dir = arrows[j].getAttribute('data-ii-arrow');
        var dead =
          (dir === 'prev' && index <= 0) ||
          (dir === 'next' && index >= this.cache.cards.length - 1);
        arrows[j].disabled = dead;
      }
    },

    onArrow: function (evt) {
      var dir = evt.currentTarget.getAttribute('data-ii-arrow');
      var next = dir === 'prev' ? this.activeIndex - 1 : this.activeIndex + 1;
      this.scrollToCard(next);
    },

    onDot: function (evt) {
      this.scrollToCard(parseInt(evt.currentTarget.getAttribute('data-ii-dot'), 10));
    },

    onDotKeydown: function (evt) {
      var next = null;

      if (evt.key === 'ArrowRight' || evt.key === 'ArrowDown') {
        next = this.activeIndex + 1;
      } else if (evt.key === 'ArrowLeft' || evt.key === 'ArrowUp') {
        next = this.activeIndex - 1;
      } else {
        return;
      }

      evt.preventDefault();

      this.scrollToCard(next);

      var dot = this.cache.dots[this.activeIndex];
      if (dot) dot.focus();
    },

    /* Scrolls the TRACK, not the page. scrollIntoView would yank the whole window
       vertically to bring a carousel the shopper is already looking at into view. */
    scrollToCard: function (index) {
      var cards = this.cache.cards;
      var track = this.cache.track;
      if (!track || !cards.length) return;

      index = Math.max(0, Math.min(index, cards.length - 1));

      var card = cards[index];
      var left =
        card.getBoundingClientRect().left -
        track.getBoundingClientRect().left +
        track.scrollLeft;

      track.scrollTo({
        left: left,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth'
      });

      this.setActive(index);
    }
  };

  /* ============================================================= Section */

  function IngredientIndex(container) {
    this.container = container;
    this.sectionId = container.getAttribute('data-section-id');

    // Every listener is recorded at bind time so onUnload can remove it with the
    // SAME reference. Without this, a customizer re-render double-binds — which
    // shows up as a chevron click opening and immediately re-closing a card.
    this.listeners = [];
    this.observers = [];
    this.scrollTriggers = [];
    this.destroyed = false;

    var setEls = container.querySelectorAll(selectors.set);
    this.panels = [];
    var self = this;
    Array.prototype.forEach.call(setEls, function (el) {
      self.panels.push(new Panel(self, el));
    });

    this.init();
  }

  IngredientIndex.prototype = {
    init: function () {
      for (var i = 0; i < this.panels.length; i++) {
        this.panels[i].init();
      }
      this.initToggle();
      this.initReveal();
    },

    /* --------------------------------------------------------------- toggle

       Morning/Night. A radiogroup: the thumb is decorative, the two radios are the
       controls. Shows one set and hides the other via the `hidden` attribute, then
       activates the now-visible panel so its carousel measures correctly. Only
       present in toggle mode — a no-op otherwise. */
    initToggle: function () {
      var group = this.container.querySelector(selectors.toggleGroup);
      if (!group) return;

      this.toggleGroup = group;
      this.setBtns = group.querySelectorAll(selectors.setBtn);

      var self = this;
      for (var i = 0; i < this.setBtns.length; i++) {
        this.on(this.setBtns[i], 'click', function (evt) {
          self.setActiveSet(evt.currentTarget.getAttribute('data-ii-set-btn'), false);
        });
        this.on(this.setBtns[i], 'keydown', this.onToggleKeydown.bind(this));
      }

      // Establish the initial state from whichever radio the Liquid marked checked
      // (Morning by default).
      var initial = group.querySelector('[data-ii-set-btn][aria-checked="true"]');
      this.activeSet = initial ? initial.getAttribute('data-ii-set-btn') : 'morning';
      group.setAttribute('data-active', this.activeSet);
    },

    onToggleKeydown: function (evt) {
      var current = evt.currentTarget.getAttribute('data-ii-set-btn');
      var other = current === 'night' ? 'morning' : 'night';

      if (evt.key === 'ArrowRight' || evt.key === 'ArrowDown') {
        evt.preventDefault();
        this.setActiveSet(current === 'morning' ? 'night' : 'morning', true);
      } else if (evt.key === 'ArrowLeft' || evt.key === 'ArrowUp') {
        evt.preventDefault();
        this.setActiveSet(current === 'night' ? 'morning' : 'night', true);
      } else if (evt.key === ' ' || evt.key === 'Enter') {
        evt.preventDefault();
        this.setActiveSet(current, false);
      }
    },

    setActiveSet: function (key, focusBtn) {
      if (!this.toggleGroup) return;

      this.activeSet = key;
      this.toggleGroup.setAttribute('data-active', key);

      var i;
      for (i = 0; i < this.setBtns.length; i++) {
        var btn = this.setBtns[i];
        var match = btn.getAttribute('data-ii-set-btn') === key;
        btn.setAttribute('aria-checked', match ? 'true' : 'false');
        btn.setAttribute('tabindex', match ? '0' : '-1');
      }

      for (i = 0; i < this.panels.length; i++) {
        var panel = this.panels[i];
        var show = panel.setKey === key;
        if (show) {
          panel.el.hidden = false;
          panel.el.setAttribute('aria-hidden', 'false');
          panel.activate();
        } else {
          panel.el.hidden = true;
          panel.el.setAttribute('aria-hidden', 'true');
        }
      }

      if (focusBtn) {
        var target = this.toggleGroup.querySelector('[data-ii-set-btn="' + key + '"]');
        if (target) target.focus();
      }
    },

    /* --------------------------------------------------------------- reveal

       Section-wide. The armed (hidden) state is added HERE, by JS, never by the
       stylesheet alone: if this script fails to load, nothing is armed and every
       card is simply visible. An invisible section is never an acceptable failure
       mode for a broken enhancement.

       In toggle mode only the Morning set carries data-ii-reveal-item (the Night
       set starts display:none and could never intersect), so this naturally leaves
       the hidden set alone. */
    initReveal: function () {
      if (this.container.getAttribute('data-ii-reveal') !== 'true') return;
      if (prefersReducedMotion()) return;

      var items = this.container.querySelectorAll(selectors.revealItem);
      if (!items.length) return;

      this.container.classList.add(classes.armed);

      var self = this;
      this.whenGsap(function (ok) {
        if (ok) {
          self.revealWithGsap(items);
        } else {
          self.revealWithObserver(items);
        }
      });
    },

    revealWithGsap: function (items) {
      var gsap = window.gsap;

      gsap.registerPlugin(window.ScrollTrigger);
      gsap.set(items, { opacity: 0, y: 18 });

      var triggers = window.ScrollTrigger.batch(items, {
        start: 'top 88%',
        once: true,
        onEnter: function (batch) {
          gsap.to(batch, {
            opacity: 1,
            y: 0,
            duration: 0.7,
            ease: 'power2.out',
            stagger: 0.08,
            overwrite: true
          });
        }
      });

      this.scrollTriggers = triggers || [];
    },

    revealWithObserver: function (items) {
      this.container.classList.add(classes.cssReveal);

      if (!('IntersectionObserver' in window)) {
        this.container.classList.remove(classes.armed);
        return;
      }

      var observer = new IntersectionObserver(
        function (entries) {
          var shown = 0;

          for (var i = 0; i < entries.length; i++) {
            if (!entries[i].isIntersecting) continue;

            var el = entries[i].target;
            el.style.setProperty('--ii-reveal-delay', shown * 80 + 'ms');
            el.classList.add(classes.in);
            shown++;

            observer.unobserve(el);
          }
        },
        { rootMargin: '0px 0px -12% 0px', threshold: 0.05 }
      );

      for (var i = 0; i < items.length; i++) {
        observer.observe(items[i]);
      }

      this.observers.push(observer);
    },

    whenGsap: function (cb) {
      if (window.gsap && window.ScrollTrigger) {
        cb(true);
        return;
      }

      var tries = 0;
      var self = this;

      this.gsapTimer = window.setInterval(function () {
        tries++;

        if (window.gsap && window.ScrollTrigger) {
          window.clearInterval(self.gsapTimer);
          self.gsapTimer = null;
          cb(true);
          return;
        }

        if (tries >= 20) {
          window.clearInterval(self.gsapTimer);
          self.gsapTimer = null;
          cb(false);
        }
      }, 50);
    },

    /* ---------------------------------------------------------------- utils */

    on: function (el, type, fn, opts) {
      if (!el) return;
      el.addEventListener(type, fn, opts);
      this.listeners.push({ el: el, type: type, fn: fn, opts: opts });
    },

    debounce: function (fn, wait) {
      var t;
      return function () {
        var args = arguments;
        var ctx = this;
        clearTimeout(t);
        t = setTimeout(function () {
          fn.apply(ctx, args);
        }, wait);
      };
    },

    /* Called by theme.Sections on shopify:section:unload. */
    onUnload: function () {
      this.destroyed = true;

      for (var p = 0; p < this.panels.length; p++) {
        if (this.panels[p].rafId) {
          window.cancelAnimationFrame(this.panels[p].rafId);
          this.panels[p].rafId = null;
        }
      }

      if (this.gsapTimer) {
        window.clearInterval(this.gsapTimer);
        this.gsapTimer = null;
      }

      this.observers.forEach(function (o) {
        o.disconnect();
      });
      this.observers = [];

      this.scrollTriggers.forEach(function (t) {
        if (t && typeof t.kill === 'function') t.kill();
      });
      this.scrollTriggers = [];

      this.listeners.forEach(function (l) {
        l.el.removeEventListener(l.type, l.fn, l.opts);
      });
      this.listeners = [];
    }
  };

  return IngredientIndex;
})();

/* theme.js builds theme.sections inside its own DOMready and then dispatches
   'page:loaded'. This file is deferred, so ordering is not guaranteed either way —
   handle both. Registering through theme.Sections is also what wires the editor
   lifecycle, and therefore what gives us onUnload. */
(function () {
  var registered = false;

  function boot() {
    if (registered) return;
    if (!window.theme || !theme.sections) return;
    registered = true;
    theme.sections.register('ingredient-index', theme.IngredientIndex);
  }

  if (window.theme && theme.sections) {
    boot();
  } else {
    document.addEventListener('page:loaded', boot);
  }
})();
